<template>
  <div class="animated fadeIn">
    <b-row class="mt-3">
      <b-col>
        <b-form-group>
          <b-form-input
            id="SearchText"
            name="SearchText"
            v-model="searchText"
            type="text"
            @input="debouncedOnChange"
            placeholder="Search across all fields"
            aria-describedby="search-text-feedback"
            autocomplete="off"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="12">
        <b-card>
          <b-card-header>
            <b-row>
              <b-col>
                <h4>Prescriber Crosswalk Records</h4>
              </b-col>
              <b-col>
                <div class="pull-right">
                  <h5>Total Records: {{searchResults.total}}</h5>
                </div>
              </b-col>
            </b-row>
          </b-card-header>
          <b-table
            striped
            hover
            :fields="fields"
            :items="searchResults.results"
            @sort-changed="changeSorting"
            :sort:by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            no-local-sorting
            small
          >
            <template slot="prescriber_npi" slot-scope="data">
              <a href @click.prevent="itemSelected(data.item)">{{ data.value }}</a>
            </template>
            <template slot="delete_button" slot-scope="data">
              <a href @click.prevent="deleteItem(data.item)">Delete</a>
            </template>
            <template slot="prescriber_old_fax_number" slot-scope="data">
              <div v-for="item in data.value" :key="item">{{item}}</div>
            </template>
          </b-table>
          <b-pagination
            align="center"
            :total-rows="rows"
            :per-page="size"
            v-model="currentPage"
            @input="changePage"
          ></b-pagination>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="2" offset-sm="10">
        <b-button class="mb-3" variant="primary" block v-on:click="create()">Add New Crosswalk</b-button>
      </b-col>
    </b-row>
    <b-modal
      id="editCrosswalk"
      ref="editCrosswalk"
      centered
      size="lg"
      @ok="handleOk"
      title="Add/Edit Crosswalk"
      :busy="isBusy"
    >
      <b-form v-if="currentRow" @submit.stop.prevent="handleSubmit">
        <b-row>
          <b-col sm="8">
            <b-row>
              <b-col>
                <b-form-group>
                  <label for="name">Prescriber NPI*</label>
                  <b-form-input
                    id="inputPrescriberNPI"
                    name="inputPrescriberNPI"
                    data-vv-as="Prescriber NPI"
                    v-model="currentRow.prescriber_npi"
                    type="text"
                    v-validate="{numeric: true, required : true, min: 10, max:10 }"
                    class="mb-1"
                    placeholder="##########"
                    aria-describedby="prescribrer-npi-feedback"
                    autocomplete="off"
                    :class="{'input': true, 'text-danger': errors.has('inputPrescriberNPI') }"
                  />

                  <div
                    class="text-danger"
                    v-show="errors.has('inputPrescriberNPI')"
                  >{{ errors.first('inputPrescriberNPI') }}</div>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group>
                  <label for="name">New Fax Number*</label>
                  <b-form-input
                    id="NewFaxNumber"
                    name="NewFaxNumber"
                    data-vv-as="New Fax Number"
                    v-model="currentRow.prescriber_fax"
                    type="text"
                    v-validate="{numeric: true, required : true, min: 10, max:10 }"
                    class="mb-1"
                    placeholder="##########"
                    aria-describedby="new-fax-number-feedback"
                    autocomplete="off"
                    :class="{'input': true, 'text-danger': errors.has('NewFaxNumber') }"
                  />
                  <div
                    class="text-danger"
                    v-show="errors.has('NewFaxNumber')"
                  >{{ errors.first('NewFaxNumber') }}</div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-form-group>
                  <label for="name">ZIP Code</label>
                  <b-form-input
                    id="ZIPCode"
                    name="ZIPCode"
                    data-vv-as="ZIP Code"
                    v-model="currentRow.prescriber_zip_code"
                    type="text"
                    v-validate="{numeric: true, min: 5 }"
                    class="mb-1"
                    placeholder="#####"
                    aria-describedby="zip-code-feedback"
                    autocomplete="off"
                    :class="{'input': true, 'text-danger': errors.has('ZIPCode') }"
                  />
                  <div
                    class="text-danger"
                    v-show="errors.has('ZIPCode')"
                  >{{ errors.first('ZIPCode') }}</div>
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group>
                  <label for="name">Address Street Number</label>
                  <b-form-input
                    id="AddrStreetNum"
                    name="AddrStreetNum"
                    data-vv-as="Address Street Number"
                    v-model="currentRow.prescriber_addr_street_number"
                    type="text"
                    v-validate="{numeric: true }"
                    class="mb-1"
                    placeholder
                    aria-describedby="address-street-number-feedback"
                    autocomplete="off"
                    :class="{'input': true, 'text-danger': errors.has('AddrStreetNum') }"
                  />
                  <div
                    class="text-danger"
                    v-show="errors.has('AddrStreetNum')"
                  >{{ errors.first('AddrStreetNum') }}</div>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <b-row>
              <b-col>
                <VItemList
                  v-if="currentRow && currentRow.prescriber_old_fax_number"
                  listLabel="Old Fax Numbers:"
                  :itemValidation="{numeric: true, min: 10, max:10 }"
                  itemValidationName="Old Fax Number"
                  itemPlaceholder="##########"
                  :items="currentRow.prescriber_old_fax_number"
                  :isReadonly="false"
                  :labelCols="0"
                  @add="(fax)=> currentRow.prescriber_old_fax_number.push(fax)"
                  @remove="(fax)=> removeOldFax(fax)"
                ></VItemList>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
    <b-modal
      id="deleteConfirmation"
      ref="deleteConfirmation"
      centered
      size="md"
      @ok="confirmDelete"
      title="Delete Confirmation"
    >
      <p>Are you sure you would like to delete this record:</p>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col>Prescriber Npi</b-col>
        <b-col>{{deleteRow.prescriber_npi}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col>New Fax Number</b-col>
        <b-col>{{deleteRow.prescriber_fax}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col>ZIP Code</b-col>
        <b-col>{{deleteRow.prescriber_zip_code}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col>Address Street Number</b-col>
        <b-col>{{deleteRow.prescriber_addr_street_number}}</b-col>
      </b-row>
      <b-row class="mb-2" v-if="deleteRow">
        <b-col>Old Fax Numbers</b-col>
        <b-col>
          <div v-for="(fax,i) in deleteRow.prescriber_old_fax_number" :key="i">{{fax}}</div>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import {
  PA_SAVE_PRESCRIBER_CROSSWALK,
  PA_SEARCH_PRESCRIBER_CROSSWALK,
  PA_DELETE_PRESCRIBER_CROSSWALK
} from "@/store/prescriber-crosswalk/actions.type";
import VItemList from "@/components/VItemList";

import * as _ from "lodash";

import { mapState } from "vuex";
export default {
  name: "PrescriberCrosswalk",
  components: { VItemList },
  data() {
    return {
      currentPage: 1,
      searchText: "",
      size: 20,
      sortBy: "prescriber_npi",
      sortDesc: false,
      currentRow: null,
      deleteRow: null,
      fields: [
        {
          key: "prescriber_npi",
          label: "Prescriber NPI",
          sortable: true
        },
        {
          key: "prescriber_fax",
          label: "New Fax Number",
          sortable: true
        },
        {
          key: "prescriber_old_fax_number",
          label: "Old Fax Number",
          sortable: false
        },
        {
          key: "prescriber_zip_code",
          label: "ZIP Code",
          sortable: true
        },
        {
          key: "prescriber_addr_street_number",
          label: "Address Street Number",
          sortable: true
        },
        {
          key: "delete_button",
          label: "",
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapState({
      isBusy: state => state.prescribercrosswalk.isBusy,
      searchResults: state => state.prescribercrosswalk.searchResults
    }),
    rows() {
      return this.searchResults.total;
    },
    modifiedFieldList() {
      return this.dataFields.filter(x => x.selected === true);
    }
  },
  methods: {
    removeOldFax(index) {
      if (!this.currentRow || !this.currentRow.prescriber_old_fax_number) {
        return;
      }

      if (index > -1 && index < this.currentRow.prescriber_old_fax_number.length) {
        this.currentRow.prescriber_old_fax_number.splice(index, 1);
      }
    },
    changeSearch() {
      this.currentPage = 1;
      this.search();
    },
    changeSorting(ctx) {
      this.sortDesc = ctx.sortDesc;
      this.sortBy = ctx.sortBy;
      this.search();
    },
    changePage(page) {
      this.currentPage = page;
      this.search();
    },
    isSuccess() {
      if (this.message === "Success") {
        this.showDismissibleAlert = true;
        return true;
      }
      this.showDismissibleAlert = false;
      return false;
    },
    onXClick(name) {
      // this.$store.dispatch(PA_CREATE_DATAOVERRIDE_CLEAR_MESSAGE);
      var item = this.dataFields.find(obj => obj.name === name);

      if (item !== undefined) {
        item.selected = false;
        item.value = "";
      }
      this.selectedFieldItem = null;
    },
    async handleSubmit() {
      const result = await this.$validator.validateAll();
      if (!result) {
        return;
      }

      if (!this.$store.state.isBusy) {
        await this.$store.dispatch(
          PA_SAVE_PRESCRIBER_CROSSWALK,
          this.currentRow
        );
        // timeout to allow Elasticsearch time to complete writing record.
        // For some reason after save the search still returns old record
        setTimeout(() => this.search(), 1000);
        this.$refs.editCrosswalk.hide();
      }
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault();
      // Trigger submit handler
      this.handleSubmit();
    },
    clear() {
      // this.dataFields.forEach(function(element) {
      //   element.selected = false;
      //   element.value = '';
      //   element.isDirty = false;
      // });
      // this.dataOverride.claim_id = '';
      // this.dataOverride.source = null;
      // this.selectedFieldItem = null;
      // this.$validator.reset();
      // this.$store.dispatch(PA_CREATE_DATAOVERRIDE_CLEAR_MESSAGE);
      // this.search();
    },
    search() {
      if (!this.$store.state.da.isSearchBusy) {
        let criteria = {
          search_text: this.searchText,
          page: this.currentPage,
          size: this.size,
          sort_desc: this.sortDesc,
          sort_by: this.sortBy
        };
        this.$store.dispatch(PA_SEARCH_PRESCRIBER_CROSSWALK, criteria);
      }
    },
    itemSelected(item) {
      this.currentRow = { ...item };
      this.currentRow.prescriber_old_fax_number = [
        ...item.prescriber_old_fax_number
      ];
      this.$refs.editCrosswalk.show();
    },
    deleteItem(item) {
      this.deleteRow = item;
      this.$refs.deleteConfirmation.show();
    },
    async confirmDelete() {
      await this.$store.dispatch(
        PA_DELETE_PRESCRIBER_CROSSWALK,
        this.deleteRow
      );
      this.deleteRow = null;
      // timeout to allow Elasticsearch time to complete writing record.
      // For some reason after save the search still returns old record
      setTimeout(() => this.search(), 1000);
      this.search();
    },
    create() {
      this.currentRow = {
        prescriber_addr_street_number: "",
        prescriber_fax: "",
        prescriber_npi: "",
        prescriber_old_fax_number: [],
        prescriber_zip_code: ""
      };
      this.$refs.editCrosswalk.show();
    }
  },
  created() {
    this.debouncedOnChange = _.debounce(this.changeSearch, 500);
  },
  beforeMount() {
    this.search();
  }
};
</script>

<style scoped>
.x-button {
  color: red;
  font-size: 25px;
}

.success-label {
  word-break: break-all;
}

.mt-10 {
  margin-top: 10px !important;
}
</style>



// WEBPACK FOOTER //
// src/views/PrescriberCrosswalk.vue